import React, { useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import { extractPatientSexKey, groupExamTipsBySystem } from "./exam2Catalog";
import Exam2SystemsTable from "./Exam2SystemsTable";
import Exam2TipsPanel from "./Exam2TipsPanel";

/**
 * Events Plan → Exam 2: systems table + differential-linked tips under each section.
 */
const Exam2PlanStep = ({
  systems = [],
  examFindings = [],
  diagnoses = [],
  loading = false,
  error = "",
  retry,
  onChangeSystemValue,
  patientContext = null,
  preferredBySystemId = {},
  onMakeDefaultNormal,
  onCreateNormalExam,
  /** When set (Management page-level Clinical Hints), hide the local tips card. */
  clinicalHintsOpen,
}) => {
  const [localTipsOpen, setLocalTipsOpen] = useState(false);
  const hintsControlled = clinicalHintsOpen !== undefined;
  const tipsOpen = hintsControlled ? Boolean(clinicalHintsOpen) : localTipsOpen;
  const sexKey = useMemo(() => extractPatientSexKey(patientContext), [patientContext]);
  const tipGroups = useMemo(
    () => groupExamTipsBySystem(examFindings, diagnoses, systems),
    [examFindings, diagnoses, systems],
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
        Document findings by system — pick multiple from the list or type and press Enter. Tab fills
        the usual normal when empty and moves to the next system (Shift+Tab goes back). Star a
        finding to make it your default normal. Abnormal findings show in red.
      </Typography>

      {error ? (
        <DataLoadErrorAlert message={error} onRetry={retry} sx={{ width: "100%", mb: 2 }} />
      ) : null}

      {loading && !systems.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!hintsControlled ? (
            <Exam2TipsPanel
              open={tipsOpen}
              onToggle={() => setLocalTipsOpen((prev) => !prev)}
              hasTips={tipGroups.hasTips}
              otherTips={tipGroups.other}
            />
          ) : null}
          {onCreateNormalExam ? (
            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
              <Button variant="outlined" size="small" onClick={onCreateNormalExam}>
                Document normal exam
              </Button>
            </Box>
          ) : null}
          <Exam2SystemsTable
            systems={systems}
            onChangeSystemValue={onChangeSystemValue}
            tipsOpen={tipsOpen}
            tipsBySystemId={tipGroups.bySystemId}
            sexKey={sexKey}
            preferredBySystemId={preferredBySystemId}
            onMakeDefaultNormal={onMakeDefaultNormal}
          />
        </>
      )}
    </Box>
  );
};

export default Exam2PlanStep;
